import { useState, useEffect, FC, ChangeEvent, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { getRequest, putRequest } from '../utils/api';
import { User } from '../interfaces/user';

const UserDetails: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchUser = async () => {
      try {
        setError(null);
        setLoading(true);
        const data = await getRequest<User>(`/users/${id}`);
        setUser(data);
        setOriginalUser(data); // Store original user data
      } catch (err: any) {
        console.error('Error fetching user:', err);
        setError(err.message || 'Failed to fetch user details.');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!user) return;
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || !originalUser) return;

    // Create an object with only changed fields
    const updatedFields: Partial<User> = {};
    Object.keys(user).forEach((key) => {
      const field = key as keyof User;
      if (user[field] !== originalUser[field]) {
        updatedFields[field] = user[field] as never;
      }
    });

    if (Object.keys(updatedFields).length === 0) {
      setSuccessMessage('No changes detected.');
      return;
    }

    console.log('Updated fields:', updatedFields);

    try {
      setError(null);
      setSuccessMessage(null);
      await putRequest(`/users/${id}`, updatedFields);
      setSuccessMessage('User details updated successfully.');
      setOriginalUser(user); // Update original user state
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user.');
    }
  };

  if (loading) return <div>Loading...</div>;

  if (error)
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );

  if (!user) return <div>User not found.</div>;

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <h2 className="text-center mb-4">User Details</h2>

          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="card shadow-sm p-4">
            <div className="mb-3">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                name="name"
                value={user.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Username</label>
              <input
                type="text"
                name="username"
                value={user.username}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Password</label>
              <input
                type="password"
                name="password"
                placeholder="Enter new password"
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={user.email}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Phone</label>
              <input
                type="text"
                name="phone"
                value={user.phone || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">User ID</label>
              <input
                type="text"
                className="form-control"
                value={user.id}
                disabled
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Created At</label>
              <input
                type="text"
                className="form-control"
                value={new Date(user.created_at).toLocaleString()}
                disabled
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Updated At</label>
              <input
                type="text"
                className="form-control"
                value={new Date(user.updated_at).toLocaleString()}
                disabled
              />
            </div>

            <button type="submit" className="btn btn-primary w-100">
              Save Changes
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
